import React from 'react';
import { Crown, Zap, BarChart, Scale } from 'lucide-react';
import { WorldMap } from '@/components/ui/world-map';
import { GridBackground } from '@/components/ui/grid-background';
import { useIsMobile } from '@/hooks/use-mobile';

// Feature item component with grid background
const FeatureItem = ({
  icon: Icon,
  title,
  description
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) => {
  const isMobile = useIsMobile();
  return <div className="relative group overflow-hidden rounded-lg transition-all duration-300 hover:shadow-md">
      <div className="absolute top-4 left-1/2 -translate-x-1/2 w-10 sm:w-12 h-10 sm:h-12 rounded-full flex items-center justify-center bg-white/10 transition-all duration-300 group-hover:bg-white/15 z-30">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-gold transition-all duration-300 group-hover:scale-110" />
      </div>
      <GridBackground title={title} description={description} showAvailability={false} className="h-full py-4 sm:py-6 px-3 sm:px-4 text-center pb-6 sm:pb-8 text-sm sm:text-base pt-16" />
    </div>;
};
const WhyChooseUs = () => {
  // Define map connection points for global presence
  const mapDots = [{
    start: {
      lat: 40.7128,
      lng: -74.0060
    },
    // New York
    end: {
      lat: 34.0522,
      lng: -118.2437
    } // Los Angeles
  }, {
    start: {
      lat: 40.7128,
      lng: -74.0060
    },
    // New York
    end: {
      lat: -15.7975,
      lng: -47.8919
    } // Brazil (Brasília)
  }, {
    start: {
      lat: 51.5074,
      lng: -0.1278
    },
    // London
    end: {
      lat: 28.6139,
      lng: 77.209
    } // New Delhi
  }, {
    start: {
      lat: 28.6139,
      lng: 77.209
    },
    // New Delhi
    end: {
      lat: -33.8688,
      lng: 151.2093
    } // Sydney
  }, {
    start: {
      lat: -33.8688,
      lng: 151.2093
    },
    // Sydney
    end: {
      lat: 35.6762,
      lng: 139.6503
    } // Tokyo
  }];
  return <section id="why-choose-us" className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-10 overflow-hidden bg-slate-50">
      {/* World Map Background - Improved visibility */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <WorldMap dots={mapDots} lineColor="#c8a87d" />
      </div>
      
      <div className="container mx-auto relative z-10">
        <div className="text-center max-w-xl mx-auto mb-8 sm:mb-12">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-luxury-100 text-luxury-800 rounded-full mb-3 sm:mb-4 font-sans">
            Why Choose Us?
          </span>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-luxury-900 font-display">
            Excellence in Every Pixel, <span className="text-gold">Without the Premium Price Tag</span>
          </h2>
          <p className="text-xs sm:text-sm md:text-base text-luxury-600 font-sans">
            Think luxury meets efficiency. Whether you need a standout web app or an internal tool to supercharge your operations, our solutions are designed to make your business scale—effortlessly.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mt-6 sm:mt-8">
          <FeatureItem icon={Crown} title="Luxury-Grade Design" description="Premium aesthetics at an unbeatable price. Every detail meticulously crafted." />
          <FeatureItem icon={Zap} title="AI-Powered Speed" description="Lightning-fast development with smart automation. Get your project done in record time." />
          <FeatureItem icon={Scale} title="Scalable Internal Systems" description="Build bespoke tools to save you hours every month and streamline your operations." />
          <FeatureItem icon={BarChart} title="Tailored Solutions" description="Custom-fit projects that evolve with your business. We create solutions that grow with you." />
        </div>
      </div>
    </section>;
};
export default WhyChooseUs;